import { useCallback } from "react"
import { Logger } from "shared/Helpers/logging"
import { CordovaPlatform, CordovaStatus } from "shared/Modules/Cordova/cordovaTypes"
import { useCordova } from "shared/Modules/Cordova/hooks/useCordova"

// iOS: URL that opens the app's page in the Settings app (UIApplication.openSettingsURLString)
const IOS_APP_SETTINGS_URL = "app-settings:"

/**
 * Opens the OS notification settings for the app. Preferred: cordova-diagnostic-plugin
 * (`cordova.plugins.diagnostic.switchToNotificationSettings`, Android + iOS). Fallback on iOS: the
 * `app-settings:` URL opened in the system browser. Returns undefined when neither is possible
 * (browser, or Android without the plugin), so callers can hide the link.
 */
export function useOpenNotificationSettings(): (() => void) | undefined {
    const cordova = useCordova()
    const logger = new Logger("notifications")

    const ready = cordova.status === CordovaStatus.DeviceReady
    const diagnostic = ready ? cordova.cordova.plugins?.diagnostic : undefined
    const switchToNotificationSettings = diagnostic?.switchToNotificationSettings
    const switchToSettings = diagnostic?.switchToSettings
    const inAppBrowser = ready ? cordova.cordova.InAppBrowser : undefined
    const isIos = ready && typeof window.device === "object" && window.device.platform?.toLowerCase() === CordovaPlatform.IOS
    const canOpenIosSettings = isIos && typeof inAppBrowser?.open === "function"

    const open = useCallback(() => {
        if (typeof switchToNotificationSettings === "function") {
            logger.info("Opening OS notification settings [diagnostic plugin]")
            switchToNotificationSettings.call(diagnostic, undefined, (error: string) => logger.warn("Failed to open notification settings", error))
        } else if (typeof switchToSettings === "function") {
            logger.info("Opening OS app settings [diagnostic plugin]")
            switchToSettings.call(diagnostic, undefined, (error: string) => logger.warn("Failed to open app settings", error))
        } else if (canOpenIosSettings && inAppBrowser) {
            logger.info("Opening OS app settings [iOS app-settings URL]")
            inAppBrowser.open(IOS_APP_SETTINGS_URL, "_system", "")
        }
    }, [diagnostic, switchToNotificationSettings, switchToSettings, canOpenIosSettings, inAppBrowser])

    const available = typeof switchToNotificationSettings === "function" || typeof switchToSettings === "function" || canOpenIosSettings
    return available ? open : undefined
}
